<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMeta } from '@/stores/meta.js'
import { fieldLabel, enumLabel, customValueText } from '@/lib/labels.js'
import { isSensitive } from '@/lib/privacy.js'
import { formatAddress, formatDateTime, formatDate, formatNumber } from '@/lib/format.js'
import TextChange from './TextChange.vue'

/** What changed in one history entry: field, old value, new value. */
const props = defineProps({ entity: { type: String, required: true }, from: { type: [Object, Array, null], default: null }, to: { type: [Object, Array, null], default: null } })
const { t } = useI18n()
const meta = useMeta()

function nameOf(key) {
  if (key.startsWith('custom_fields.')) return `cf.${key.slice(14)}`
  if (key === 'owner_user' || key === 'owner_group') return 'owner'
  // A reference column ("regarding_id") is shown as its field; a field ending in "_id" of its
  // own ("tax_id") stays as it is.
  return meta.field(props.entity, key) ? key : key.replace(/_id$/, '')
}
// Bookkeeping that says nothing to a person.
const TECHNICAL = ['version', 'updated_at', 'search_text', 'regarding_type', 'noteable_type', 'probability_is_manual']
function text(key, value) {
  if (value === null || value === undefined || value === '') return '—'
  const name = nameOf(key)
  if (name.startsWith('cf.')) return customValueText(meta.customField(props.entity, name.slice(3)), value) || '—'
  if (typeof value === 'boolean') return value ? t('common.yes') : t('common.no')
  if (Array.isArray(value)) return value.map((v) => (v && typeof v === 'object' ? v.label || v.value || v.name || '' : v)).filter(Boolean).join(', ') || '—'
  if (typeof value === 'object') {
    if ('label' in value) return value.label
    if ('street' in value || 'city' in value) return formatAddress(value)
    return JSON.stringify(value)
  }
  // A link's role and influence (stakeholders, contacts of an account).
  if (key === 'role') return meta.dictionaryLabel(props.entity === 'opportunity' ? 'stakeholder_role' : 'contact_role', value)
  if (key === 'influence') return meta.dictionaryLabel('stakeholder_influence', value)
  // What the other side of a connection is (D-202).
  if (key === 'connection_role') return meta.dictionaryLabel('connection_role', value)
  const field = meta.field(props.entity, name)
  if (field?.type === 'dictionary') return meta.dictionaryLabel(field.dictionary, value)
  if (field?.type === 'enum') return enumLabel(props.entity, name, value)
  if (field?.type === 'datetime') return formatDateTime(value)
  if (field?.type === 'date') return formatDate(value)
  if (field?.type === 'money') return formatNumber(value, 2)
  if (field?.type === 'percent') return `${formatNumber(value)}%`
  if (field?.type === 'decimal' || field?.type === 'integer') return formatNumber(value)
  return String(value)
}
// A formatted text (TipTap's document), as kept — or as older entries kept it, a JSON string.
function docOf(value) {
  if (value && typeof value === 'object' && value.type === 'doc') return value
  if (typeof value === 'string' && value.startsWith('{"type":"doc"')) {
    try {
      return JSON.parse(value)
    } catch {
      return null
    }
  }
  return null
}
const isDoc = (key) => meta.field(props.entity, nameOf(key))?.type === 'richtext' || docOf(props.from?.[key]) || docOf(props.to?.[key])
const rows = computed(() => {
  const keys = new Set([...Object.keys(props.from || {}), ...Object.keys(props.to || {})])
  return [...keys].filter((k) => !TECHNICAL.includes(k)).map((k) => isDoc(k) ? {
    key: k,
    label: fieldLabel(props.entity, nameOf(k)),
    doc: { from: docOf(props.from?.[k]), to: docOf(props.to?.[k]) },
    from: '', to: '',
    private: isSensitive(meta, props.entity, nameOf(k)),
  } : ({
    key: k,
    label: fieldLabel(props.entity, nameOf(k)),
    from: text(k, props.from?.[k]),
    to: text(k, props.to?.[k]),
    private: isSensitive(meta, props.entity, nameOf(k)),
  })).filter((row) => row.doc || row.from !== '—' || row.to !== '—')
})
</script>

<template>
  <ul v-if="rows.length" class="space-y-0.5 text-xs">
    <li v-for="row in rows" :key="row.key" class="flex flex-wrap items-baseline gap-1">
      <span class="font-medium text-muted">{{ row.label }}:</span>
      <!-- a formatted text: what changed in it, read as text -->
      <TextChange v-if="row.doc" :from="row.doc.from" :to="row.doc.to" :label="row.label" :sensitive="row.private" />
      <template v-else>
      <span v-private="row.private" class="text-faint line-through decoration-1">{{ row.from }}</span>
      <i class="fa-solid fa-arrow-right text-[9px] text-faint" aria-hidden="true"></i>
      <span v-private="row.private">{{ row.to }}</span>
      </template>
    </li>
  </ul>
</template>
